<template>
    <div class="JobOverview text-white relative text-center">
        <!-- Action buttons -->
        <div v-if="!showOnlySteps" class="JobOverview__controls">
            <button
                v-if="['accepted', 'started'].indexOf(status) !== -1"
                type="button"
                class="btn btn-default text-base relative text-gray-700"
                title="Afbryd samarbejde"
                :disabled="loading"
                @click="cancel"
            >
                <i class="fa fa-ban"></i>
            </button>
            <ChatModal
                v-if="spark.client === 'web'"
                class="inline-block"
                :refresh="refreshChat"
                :thread-id="chatThreadId"
            >
                <template v-slot:default="{ open, loading, unread }">
                    <button
                        type="button"
                        class="btn btn-primary text-lg fixed bottom-[80px] right-[20px] z-10 shadow-lg"
                        title="Åbn beskeder"
                        :disabled="loading"
                        :data-init="initializeChat(open)"
                        @click="open"
                    >
                        <i class="fa fa-envelope"></i>
                        <span
                            v-if="!loading && unread.length > 0"
                            class="Badge Badge--red absolute top-0 right-0"
                            v-text="unread.length"
                        />
                    </button>
                </template>
            </ChatModal>
        </div>
        <div class="container py-8">
            <!-- Headline and progress bar  -->
            <div class="max-w-5xl mx-auto">
                <h2 class="h3 leading-normal mb-3" v-if="!showOnlySteps">
                    <span class="text-white uppercase" style="font-style: normal;" v-text="currentStep.headline" />
                    <span class="not-italic" v-text="currentStep.emoji" />
                </h2>

                <p v-if="!showOnlySteps" class="text-sm mb-5" v-html="currentStep.description" />
                <div class="range-block">
                    <RangeSlider :class="progressBarColor" :value="progressBarWidth" :disabled="true" />

                    <div class="range-table" v-if="!hasFailed">
                        <table v-if="status !== 'content_denied'" class="table-fixed w-full mt-5 text-sm">
                            <tr>
                                <td
                                    v-for="step of ['applied', 'accepted'/* content_denied */, 'content_received', 'started', 'finished']"
                                    :key="steps[step].label"
                                    v-text="steps[step].label"
                                />
                            </tr>
                        </table>

                        <table v-if="status === 'content_denied'" class="table-fixed w-full mt-5 text-sm">
                            <tr>
                                <td
                                    v-for="step of ['applied', 'content_denied', 'content_received', 'started', 'finished']"
                                    :key="steps[step].label"
                                    v-text="steps[step].label"
                                />
                            </tr>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Individual slot for brand vs influencer -->
            <div class="mt-5" v-if="!showOnlySteps">
                <slot v-bind="slotScope" />
            </div>

            <template v-if="!loading && !showOnlySteps">
                <!-- Common slot for brand and influencer -->
                <template v-if="status === 'finished'">
                    <div class="mt-5 py-5 border-t border-b border-gray-300">
                        <div v-if="job.content_url">
                            Link til opslag:
                            <a :href="job.content_url" target="_BLANK">{{ job.content_url }}</a>
                        </div>
                        <div>
                            <div v-for="(url, i) in urls">
                                Link til opslag #{{ i+1 }}:
                                <a :href="url" target="_BLANK">{{ url }}</a>
                            </div>
                        </div>
                        <ContentsGrid
                            :contents="contents"
                            :color="'white'"
                            content-class="border border-gray-300 rounded-lg bg-gray-100"
                        />
                    </div>

                    <div class="mt-5 text-left">
                        <slot name="review-form" v-bind="slotScope" />

                        <ReviewCard
                            v-for="review of job.reviews"
                            :key="review.id"
                            class="max-w-2xl mx-auto mb-2 bg-gray-100 border border-gray-300 rounded-lg p-3"
                            v-bind="{ review }"
                        ></ReviewCard>
                    </div>
                </template>

                <!-- Individual step specific slot -->
                <div v-if="hasSlot(status)" class="mt-5">
                    <slot :name="status" v-bind="slotScope" />
                </div>
            </template>
        </div>
    </div>
</template>
<style lang="less" type="text/less">
@import '~dashboard-less/variables';

.JobOverview__controls {
    @apply sticky -mb-8 top-0 inset-x-0 z-10 text-right p-3;
}

body.--agent-web .JobOverview__controls {
    @apply top-16;
}

@screen sm {
    .JobOverview__controls {
        @apply static -mb-12;
    }
}

.JobOverview {
    .range-slider-fill,
    .range-slider-knob {
        transition: all 0.6s ease;
    }
}

.JobOverview .JobOverview__btn {
    @apply w-full mb-4;
}

@screen sm {
    .JobOverview .JobOverview__btn {
        @apply w-auto mb-auto mx-2;
    }
}
@media (max-width: 768px){
    .range-block {
        position: relative;
        padding-top: 100%;
        max-height: 400px;
        .range-slider {
            position: absolute;
            top: 0;
            left: 15px;
            right: 0;
            transform: rotate(90deg);
            transform-origin: left top;
        }
        .range-table {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            left: 20px;
            .table-fixed {
                height: 100%;
                position: relative;
                margin-top: 0;
                tr {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    text-align: left;
                    align-items: stretch;
                    height: 100%;
                }
            }
        }
    }
}
</style>

<script type="text/babel">
import { ReplaySubject } from 'rxjs'
import ChatModal from 'dashboard-js/components/chat/ChatModal'
import HasLoadingState from 'dashboard-js/mixins/HasLoadingState'
import HasSubscriptions from 'dashboard-js/mixins/HasSubscriptions'
import RangeSlider from 'dashboard-js/components/utilities/RangeSlider'
import RestoreOnPageBack from 'dashboard-js/mixins/RestoreOnPageBack'
import ReviewCard from 'dashboard-js/components/reviews/ReviewCard'
import orderBy from 'lodash-es/orderBy'

export default {
    components: { ChatModal, ReviewCard, RangeSlider },

    mixins: [HasLoadingState, HasSubscriptions, RestoreOnPageBack],

    props: ['chatThreadId', 'dataJob', 'steps', 'showOnlySteps'],

    data() {
        return {
            job: this.dataJob,
            initializing: true,
            initializedChat: false,
            refreshChat: new ReplaySubject(1),
        }
    },

    computed: {
        contents() {
            return orderBy(this.$get(this.job, 'contents', []), 'media_published_at')
        },

        currentStep() {
            return this.steps[this.status]
        },

        hasFailed() {
            return ['cancelled', 'declined'].indexOf(this.status) !== -1
        },

        hasSucceeded() {
            return ['finished'].indexOf(this.status) !== -1
        },

        progressBarColor() {
            if (this.hasFailed) {
                return '--danger'
            }
            if (this.hasSucceeded) {
                return '--success'
            }
            return ''
        },

        progressBarWidth() {
            if (this.initializing) {
                return 0
            }

            let stepSize = ['applied', 'accepted', 'content_received', 'started', 'finished'].indexOf(this.status) + 1

            if (['cancelled', 'declined'].indexOf(this.status) !== -1) {
                stepSize = 5
            }

            if (this.status === 'content_denied') {
                stepSize = 2
            }

            return stepSize === 5 ? 100 : stepSize * 20 - 10 - 1
        },

        slotScope() {
            return {
                job: this.job,
                loading: this.loading,
                status: this.status,
                warn: this.warn,
            }
        },

        status() {
            return this.job.status
        },

        urls() {
            return this.job.content_urls
        },
    },

    watch: {
        status() {
            // When status change, we might want to lock the conversation.
            this.refreshChat.next()
        },
    },

    created() {
        this.load(this.componentWasRestored)

        this.subscribe(Echo.private(`jobs.${this.job.id}`)).listen(
            'Services\\Jobs\\Events\\JobUpdated',
            () => {
                // Load from server again to include all necessary relations
                this.load(true)
            }
        )

        Bus.$on('jobUpdated', job => {
            if (!job) {
                return this.load(true)
            }
            Object.assign(this.job, job)
        })
    },

    mounted() {
        setTimeout(() => {
            this.initializing = false
            this.saveState()
        }, 1000)
    },

    methods: {
        initializeChat(open) {
            if (!this.initializedChat && window.location.hash.endsWith('messages')) {
                this.initializedChat = true
                open()
            }
        },

        cancel() {
            const message =
                Spark.userId === this.job.influencer_id
                    ? 'Advarsel. Vælger du at afbryde samarbejdet, vil alt kontakt til virksomheden stoppe. Du vil ikke få betaling for denne opgave.'
                    : 'Advarsel. Vælger du at afbryde samarbejdet, vil alt kontakt til influenten stoppe. Du vil få 100% af den aftalte betaling til influenten refunderet.'

            this.warn(message, 'Ja, afbryd', () => {
                axios
                    .post(route('api.job.action', [this.job.id, 'cancel']))
                    .then(response => Bus.$emit('jobUpdated', response.data.data))
                    .then(this.finishLoading())
                    .then(this.saveState)
            })
        },

        hasSlot(name) {
            return typeof this.$scopedSlots[name] !== 'undefined'
        },

        load(silently) {
            axios
                .get(`/api/jobs/${this.job.id}`)
                .then(response => Bus.$emit('jobUpdated', response.data.data))
                .then(this.finishLoading(silently))
                .then(this.saveState)
        },

        warn(message, acceptBtn, action) {
            swal(
                {
                    title: 'Er du sikker?',
                    text: message,
                    customClass: 'danger',
                    showCancelButton: true,
                    confirmButtonText: acceptBtn,
                    cancelButtonText: 'Fortryd',
                    closeOnConfirm: true,
                    allowEscapeKey: true,
                    allowClickOutside: true,
                    type: 'warning',
                },
                action
            )
        },
    },
}
</script>
